import { z } from 'zod';

export const currencyCodeSchema = z
  .string()
  .regex(/^[A-Z]{3}$/, 'currency must be an ISO 4217 uppercase code');

export const finiteNumberSchema = z
  .number()
  .refine((value) => Number.isFinite(value), {
    message: 'value must be a finite number',
  });

export const nonNegativeNumberSchema = finiteNumberSchema.refine(
  (value) => value >= 0,
  {
    message: 'value must be greater than or equal to 0',
  }
);

export const percentageValueSchema = finiteNumberSchema;

export const moneyAmountSchema = z.object({
  amount: finiteNumberSchema,
  currency: currencyCodeSchema,
});

export const nullableMoneyAmountSchema = moneyAmountSchema.nullable();

export const vatRateSchema = z.object({
  id: z.string().min(1).nullable(),
  percent: percentageValueSchema.nullable(),
  source: z
    .enum(['cost', 'price', 'legacy', 'manual', 'organization', 'unknown'])
    .optional(),
});

export const moneyPrecisionModeSchema = z.enum(['currency-js-compatible']);

export const moneyRoundingModeSchema = z.enum([
  'currency-js',
  'legacy-smart-round',
  'none',
]);

export const moneyPrecisionSchema = z.object({
  mode: moneyPrecisionModeSchema,
  decimalPlaces: z.number().int().min(0).max(8),
  roundingMode: moneyRoundingModeSchema,
  preserveCurrencyJsSemantics: z.literal(true),
});

export const calculatedMoneyFieldsSchema = z.object({
  currency: currencyCodeSchema,
  sourceCurrency: currencyCodeSchema.optional(),
  cost: finiteNumberSchema.nullable(),
  netCost: finiteNumberSchema.nullable(),
  unitPrice: finiteNumberSchema.nullable(),
  // Raw per-unit price in `sourceCurrency` (before exchange-rate
  // conversion). Persistence uses this so legacy price JSON keeps
  // item-currency values; display keeps using the converted `unitPrice`.
  sourceUnitPrice: finiteNumberSchema.nullable().optional(),
  finalPrice: finiteNumberSchema.nullable(),
  finalPriceWithTax: finiteNumberSchema.nullable(),
  profit: finiteNumberSchema.nullable(),
  markupPercent: percentageValueSchema.nullable(),
  commissionPercent: percentageValueSchema.nullable(),
  units: finiteNumberSchema.nullable(),
  costVat: vatRateSchema.nullable(),
  priceVat: vatRateSchema.nullable(),
  isGross: z.boolean(),
  useNetCost: z.boolean(),
  rate: finiteNumberSchema.optional(),
  finalCost: finiteNumberSchema.nullable().optional(),
  // Supplier net cost for the WHOLE line — `netCost` multiplied by every unit
  // the line bills (rooms × nights, travellers, rental days), in the SOURCE
  // currency like `netCost` itself. `netCost` alone is the per-unit rate, so a
  // 3-night stay published only the nightly figure and consumers that meant
  // "what we owe the supplier" printed a third of it (XT-269).
  //
  // Distinct from `finalCost`, which is the GROSS cost (VAT in, commission in)
  // converted into the base currency. This one keeps the net basis: VAT
  // excluded and supplier commission deducted.
  finalNetCost: finiteNumberSchema.nullable().optional(),
});

export type CurrencyCode = z.infer<typeof currencyCodeSchema>;
export type MoneyAmount = z.infer<typeof moneyAmountSchema>;
export type VatRate = z.infer<typeof vatRateSchema>;
export type MoneyPrecision = z.infer<typeof moneyPrecisionSchema>;
export type CalculatedMoneyFields = z.infer<typeof calculatedMoneyFieldsSchema>;
